// What was thrown away, and what it said.
//
// A dismissal is usually a misclick, and the only way to judge whether it was
// one is to read what the recording actually captured. So this is a two-pane
// modal rather than a list: the recordings down the left, the transcript of
// whichever is selected on the right.
//
// The window is seven days. That isn't a UI decision — it's how long the
// retention sweep keeps a dismissed recording's clinical content before nulling
// it (see syncToRetentionService). Listing rows past that point would mean
// showing dates with nothing behind them, so the list and the content expire
// together.

import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';

const fmtWhen = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unknown date';

const fmtDuration = (ms) => {
  if (!ms || ms < 1000) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const daysLeft = (until) => {
  if (!until) return null;
  const ms = new Date(until).getTime() - Date.now();
  if (ms <= 0) return null;
  return Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

export default function DismissedCapturesModal({ show, onHide, captures = [], onRestore }) {
  const [selectedId, setSelectedId] = useState(null);

  // Open on the most recent, and follow the list when a restore removes the
  // row that was being read.
  useEffect(() => {
    if (!show) return;
    const stillThere = captures.some((c) => String(c._id) === String(selectedId));
    if (!stillThere) setSelectedId(captures[0] ? String(captures[0]._id) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, captures]);

  const selected = useMemo(
    () => captures.find((c) => String(c._id) === String(selectedId)) || null,
    [captures, selectedId],
  );

  // The cleaned write-up is what the practitioner would have applied; the raw
  // transcript is the fallback when cleanup failed or was never reached.
  const body = selected?.cleaned || selected?.transcript || '';
  const remaining = daysLeft(selected?.dismissedVisibleUntil);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header
        closeButton
        style={{ background: '#CFE2FF' }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">Dismissed recordings</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="d-flex" style={{ minHeight: 320 }}>
          {/* ── The recordings ─────────────────────────────────────────── */}
          <div
            className="border-end p-2 d-flex flex-column gap-1"
            style={{ width: 230, flexShrink: 0, overflowY: 'auto', maxHeight: 460 }}
          >
            {captures.length === 0 && (
              <p className="text-muted small px-1 py-2 mb-0">Nothing dismissed.</p>
            )}
            {captures.map((c) => {
              const active = String(c._id) === String(selectedId);
              return (
                <Button
                  key={c._id}
                  variant=""
                  className="rounded-0 text-start d-flex flex-column align-items-start"
                  style={{
                    backgroundColor: active ? '#cfe2ff' : '#fff',
                    borderColor: active ? '#6eaaee' : '#dee2e6',
                    color: '#0f3490',
                    fontWeight: active ? 600 : 'normal',
                  }}
                  onClick={() => setSelectedId(String(c._id))}
                >
                  <span className="text-truncate w-100">
                    {c.mode === 'template' ? 'Template recording' : 'Recording'}
                  </span>
                  <span className="small text-muted">{fmtWhen(c.createdAt)}</span>
                </Button>
              );
            })}
          </div>

          {/* ── What it said ───────────────────────────────────────────── */}
          <div className="p-3 flex-grow-1" style={{ minWidth: 0, overflowY: 'auto', maxHeight: 460 }}>
            {!selected && (
              <p className="text-muted mb-0">Pick a recording to read its transcription.</p>
            )}
            {!!selected && (
              <>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  <h6 className="mb-0 text-primary fw-bold">
                    {selected.mode === 'template' ? 'Template recording' : 'Recording'}
                  </h6>
                  <Badge bg="secondary">{fmtWhen(selected.createdAt)}</Badge>
                  {!!fmtDuration(selected.durationMs) && (
                    <span className="small text-muted">{fmtDuration(selected.durationMs)}</span>
                  )}
                </div>
                {remaining != null && (
                  <p className="small text-muted mb-2">
                    Recoverable for {remaining} more day{remaining === 1 ? '' : 's'}, after which
                    the transcription is permanently removed.
                  </p>
                )}
                {body ? (
                  <div
                    className="small"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}
                  >
                    {body}
                  </div>
                ) : (
                  <p className="text-muted mb-0">
                    {selected.status === 'FAILED'
                      ? "This recording never transcribed, so there's nothing to read."
                      : 'No transcription was stored for this recording.'}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          disabled={!selected}
          style={{ minWidth: 140 }}
          onClick={() => selected && onRestore?.(selected)}
        >
          <i className="fa-solid fa-rotate-left me-2" />
          Restore
        </Button>
        <Button variant="outline-secondary" style={{ minWidth: 140 }} onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
